import React from 'react';
import { Link } from 'react-router-dom';
import SubPage from '../components/site/SubPage';
import { PageSection, PageTitleImage } from '../components/site/SubPageParts';
import { ROUTES } from '../components/site/siteNav';

const IMG = '/images/site/company';
const ORANGE = '#E2762B';

const SPOTLIGHTS = [
  {
    month: 'January',
    name: 'St. Jude',
    url: 'https://www.stjude.org',
    body: 'Advance cures and means of prevention for pediatric catastrophic diseases through research and treatment. Consistent with Danny Thomas’ vision, no child is denied treatment based on race, religion, or a family’s ability to pay.',
  },
  {
    month: 'February',
    name: 'Tunnel to Towers',
    url: 'https://t2t.org',
    body: 'Honors the sacrifice of FDNY firefighter Stephen Siller and all those who laid down their lives to save others on September 11, 2001, while supporting military and first responders who continue to make the supreme sacrifice of life and limb for our country by providing mortgage-free homes.',
  },
  {
    month: 'March',
    name: 'Wounded Warrior',
    url: 'https://www.woundedwarriorproject.org',
    body: 'Aims to raise awareness and enlist public support for the needs of severely injured service members.',
  },
  {
    month: 'April',
    name: 'Shriners Children’s',
    url: 'https://www.shrinerschildrens.org',
    body: 'Dedicated to providing the highest quality care to children with specialized healthcare needs, conducting research, and educating healthcare professionals, all within a compassionate and family-centered environment.',
  },
  {
    month: 'May',
    name: 'USO',
    url: 'https://www.uso.org',
    body: 'Since 1941, the USO has been the nation’s leading organization to serve the men and women in the U.S. military, and their families, throughout their time in uniform. From the moment they join, through their assignments and deployments, and as they transition back to their communities, the USO is here to strengthen the well-being of the people serving in America’s military and their families.',
  },
  {
    month: 'June',
    name: 'Independence Fund',
    url: 'https://independencefund.org',
    body: 'Supports injured and wounded Veterans with the tools to achieve the independence and freedoms they fought so hard to preserve, across three pillars: mobility, caregivers, and wellness. The Independence Fund is dedicated to empowering the Warfighter community by fostering the physical, mental, emotional, and spiritual independence of service members, combat Veterans, their caregivers, families, and the Allies who served alongside them.',
  },
  {
    month: 'July',
    name: 'Mercy Ships',
    url: 'https://www.mercyships.org',
    body: 'Provides hope and healing to those in need by delivering free healthcare services through hospital ships filled with volunteer medical professionals, providing life-changing surgeries to children and adults who would otherwise go without care.',
  },
  {
    month: 'August',
    name: 'Samaritan’s Purse',
    url: 'https://www.samaritanspurse.org',
    body: 'A nondenominational evangelical Christian organization providing spiritual and physical aid to hurting people around the world. Since 1970, Samaritan’s Purse has helped meet the needs of people who are victims of war, poverty, natural disasters, disease, and famine, with the purpose of sharing God’s love through His Son, Jesus Christ. The organization serves the Church worldwide to promote the Gospel of the Lord Jesus Christ.',
  },
  {
    month: 'September',
    name: 'American Cancer Society',
    url: 'https://www.cancer.org',
    body: 'Improve the lives of people with cancer and their families through advocacy, research, and patient support, to ensure everyone has an opportunity to prevent, detect, treat, and survive cancer.',
  },
  {
    month: 'October',
    name: 'Our Rescue',
    url: 'https://www.ourrescue.org',
    body: 'To end human trafficking and child exploitation while empowering survivors to reclaim their lives and thrive on their healing journey.',
  },
  {
    month: 'November',
    name: 'American Heart Association',
    url: 'https://www.heart.org',
    body: 'Strives to improve cardiovascular health and reduce the impact of heart disease and stroke worldwide.',
  },
  {
    month: 'December',
    name: 'ALS Association',
    url: 'https://www.als.org',
    body: 'To make ALS livable, and to cure it.',
  },
];

const Philanthropy = () => (
  <SubPage title="Philanthropy Spotlight">
    <PageTitleImage
      src={`${IMG}/philanthropy-hero.jpeg`}
      alt="Cupped hands holding a chain of paper people in many colours"
    >
      Serving a
      <br />
      Higher Purpose
    </PageTitleImage>

    {/* ---------------- Mission ---------------- */}
    <PageSection className="pt-8">
      <h2 className="text-[24px] font-bold text-black md:text-[30px]">Mission.</h2>
      <div className="mt-3 space-y-4 text-[15px] leading-relaxed text-black md:text-[18px]">
        <p>Each month, 10% of our subscription community revenue goes to a designated charity.</p>
        <p>
          We don&rsquo;t wait for the right people to find us. We actively seek out nonprofit and
          social impact organizations that are advancing bold, modern solutions to some of the most
          pressing challenges facing humanity today.
        </p>
      </div>
    </PageSection>

    {/* ---------------- Monthly spotlights ---------------- */}
    <PageSection className="pt-8">
      <ul>
        {SPOTLIGHTS.map((item) => (
          <li key={item.month} className="border-b border-black/20 py-6 first:pt-0">
            <h3 className="flex flex-wrap items-baseline gap-x-3 text-[20px] font-bold md:text-[27px]">
              <span aria-hidden="true" style={{ color: ORANGE }}>
                &rarr;
              </span>
              <span className="text-black">{item.month}</span>
              <span style={{ color: ORANGE }}>/</span>
              <span className="text-black">{item.name}</span>
              <span style={{ color: ORANGE }}>/</span>
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="text-black underline decoration-1 underline-offset-2 hover:text-[#0A6EA8]"
              >
                {item.url.replace('https://', '')}
              </a>
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-black md:text-[17px]">{item.body}</p>
          </li>
        ))}
      </ul>
    </PageSection>

    {/* ---------------- Explore / Join us ---------------- */}
    <PageSection className="pt-10">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
        <div className="border border-black/25 px-6 py-7 md:px-8 md:py-9">
          <h2 className="flex items-center gap-4 text-[28px] font-bold text-black md:text-[36px]">
            Explore
            <span aria-hidden="true" style={{ color: ORANGE }}>
              &darr;
            </span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-black md:text-[17px]">
            When it comes to philanthropy, we don&rsquo;t wait for the right people to find us. We
            actively seek out nonprofit and social impact organizations that are advancing bold,
            future-ready solutions to some of the most pressing challenges facing humanity today.
          </p>
        </div>

        <div className="border border-black/25 px-6 py-7 md:px-8 md:py-9">
          <h2 className="flex items-center gap-4 text-[28px] font-bold text-black md:text-[36px]">
            Join Us
            <span aria-hidden="true" style={{ color: ORANGE }}>
              &darr;
            </span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-black md:text-[17px]">
            If your organization would like to be considered, please complete and submit the
            philanthropy package.
          </p>
          <Link to={ROUTES.philanthropyGrants} className="mt-5 inline-flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-8 w-10 items-center justify-center text-[17px] text-white"
              style={{ backgroundColor: ORANGE }}
            >
              &gt;
            </span>
            <span className="text-[16px] text-black underline md:text-[18px]">package</span>
          </Link>
        </div>
      </div>
    </PageSection>
  </SubPage>
);

export default Philanthropy;
